import { ColumnChart, Serie } from "@iliotech/widgets";
import { Point } from "@progress/kendo-drawing/geometry";
import {
  TooltipContext,
  AxisLabelVisualArgs,
  RenderEvent,
} from "@progress/kendo-react-charts";
import { geometry, Group, Text, Path } from "@progress/kendo-drawing";
import { CSSProperties } from "react";

export interface HistoricReturnChartProps {
  chartData: Serie[];
  categories: number[];
  mean: number;
  getUpperBound: (lowerBound: number) => number;
  chartStyle?: CSSProperties;
  generating?: boolean;
  height?: number;
  width?: number;
}

const DailyReturnsChartTooltipRenderer =
  (getUpperBound: (lowerBound: number) => number) =>
  (context: TooltipContext) => {
    console.log({ context });
    const lowerBound = Number(
      (context?.point?.category as string).replace("%", "")
    );

    const upperBound = getUpperBound(lowerBound);

    return (
      <div>
        <div style={{ fontSize: "1.2rem" }}>
          {lowerBound} to {upperBound} occurs {context.point?.value}% of the
          time
        </div>
      </div>
    );
  };

const DailyReturnsChartTicksVisual =
  (categories: number[]) => (e: AxisLabelVisualArgs) => {
    const group = new Group();
    const name = e.text;
    const offset = name.replace("-", "").length * 2.4;

    // Create text with adjusted properties for better PDF export
    const text = new Text(
      name,
      new Point(e.rect.origin.x - offset, e.rect.origin.y),
      {
        font: "12px 'DM Sans', Nunito, sans-serif",
        fill: { color: "var(--color)" }, // Use fill instead of stroke for text
      }
    );

    group.append(text);

    if (categories.length > 30) {
      group.transform(
        new geometry.Transformation().rotate(
          90,
          new Point(
            text.position().getX() + offset,
            text.position().getY() + offset
          )
        )
      );
    }

    return group;
  };

function nextLowerHalf(number: number) {
  if (number >= 0) {
    return Math.floor(number * 2) / 2;
  } else {
    return Math.ceil(number * 2) / 2;
  }
}

export const HistoricReturnChart = ({
  chartData,
  categories,
  mean,
  getUpperBound,
  chartStyle,
  generating = false,
  height,
  width,
}: HistoricReturnChartProps) => {
  const drawMeanLine = (ev: RenderEvent) => {
    const chart = ev.target.chartInstance;
    const foundCategory = categories.find(
      (item) => item === nextLowerHalf(mean)
    );

    if (!chart || !mean) {
      return;
    }

    // get the axes
    const xAxis = chart.findAxisByName("categoryAxis");

    if (!xAxis) {
      return;
    }

    const meanX = xAxis.slot(foundCategory + "%");

    const offSet = (mean - foundCategory!) * 80;

    const greenLine = new Path({
      stroke: {
        color: "purple",
        width: 2,
      },
    })
      .moveTo(meanX.origin.x + offSet, meanX.origin.y)
      .lineTo(meanX.origin.x + offSet, -meanX.origin.y);

    const group = new Group();
    group.append(greenLine);

    chart.surface.draw(group);
  };

  const valueFormatter = (value: number) => `${value?.toFixed(0)}`;
  return (
    <div
      className={"chart-resizable"}
      style={{
        width: "100%",
        overflow: "scroll",
        overflowY: "hidden",
      }}
    >
      <ColumnChart
        generating={generating}
        seriesGap={0.4}
        tooltipRenderer={DailyReturnsChartTooltipRenderer(getUpperBound)}
        stacked={true}
        chartStyle={{
          width: "100%",
          minWidth: width || 800,
          ...chartStyle,
        }}
        height={height || 250}
        width={width}
        valueAxisVisible={false}
        items={chartData}
        categoryAxisProps={{
          labels: {
            visual: DailyReturnsChartTicksVisual(categories),
          },
        }}
        categories={categories.map((item) => item + "%")}
        valueAxisFormatter={valueFormatter}
        onRender={drawMeanLine}
      />
    </div>
  );
};
